import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import type { InputHTMLAttributes, KeyboardEvent, ReactNode } from 'react'
import { Search, X } from 'lucide-react'
import { clsx } from 'clsx'
import styles from './SearchInput.module.css'

type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size' | 'type' | 'aria-label'>

export interface SearchInputProps extends NativeInputProps {
  value: string
  onValueChange: (value: string) => void
  /** What the box is for. Required: a placeholder disappears as soon as someone types. */
  ariaLabel: string
  /** Runs for the × button and for Escape. Defaults to `onValueChange('')`. */
  onClear?: () => void
  /** `sm` is for search boxes inside popovers and table toolbars. */
  size?: 'md' | 'sm'
  /** Spinner in place of the × while results load. */
  loading?: boolean
  /** Text beside the box, e.g. "Searching…". Announced to screen readers. */
  status?: ReactNode
  /** `/` anywhere on the page focuses this box. Only for a page's main search. */
  focusOnSlash?: boolean
  /** Class for the outer element; use it for width and margins, not for the box's look. */
  className?: string
  /** Controls after the box's text, inside its border, e.g. a filter button. */
  trailing?: ReactNode
}

function isTypingElsewhere(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null
  if (!element?.tagName) return false
  return element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
}

/**
 * The one search box. Every search on the site renders this so they all look the same and behave
 * the same: icon on the left, × once there is text, Escape clears, and 16px text so iOS does not
 * zoom the page on focus. It owns none of the search itself; debouncing, URL state and results
 * stay with the page (see `useDebouncedSearchCommit`, `useDebouncedValue`).
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    onValueChange,
    ariaLabel,
    onClear,
    size = 'md',
    loading = false,
    status,
    focusOnSlash = false,
    className,
    trailing,
    onKeyDown,
    ...inputProps
  },
  forwardedRef,
) {
  const inputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement)

  useEffect(() => {
    if (!focusOnSlash) return
    const onDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return
      if (isTypingElsewhere(event.target)) return
      event.preventDefault()
      inputRef.current?.focus()
      inputRef.current?.select()
    }
    document.addEventListener('keydown', onDocumentKeyDown)
    return () => document.removeEventListener('keydown', onDocumentKeyDown)
  }, [focusOnSlash])

  const clear = () => {
    if (onClear) onClear()
    else onValueChange('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented) return
    if (event.key === 'Escape' && value) {
      event.preventDefault()
      clear()
    }
  }

  return (
    <div className={clsx(styles.root, className)}>
      <div className={clsx(styles.box, size === 'sm' && styles.small)}>
        <Search className={styles.icon} size={size === 'sm' ? 15 : 18} strokeWidth={2} aria-hidden />
        <input
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="search"
          {...inputProps}
          ref={inputRef}
          type="text"
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={handleKeyDown}
          className={styles.input}
          aria-label={ariaLabel}
        />
        {loading && <span className={styles.spinner} role="status" aria-label="Searching" />}
        {!loading && value && (
          <button
            type="button"
            className={styles.clear}
            aria-label="Clear search"
            onClick={() => {
              clear()
              inputRef.current?.focus()
            }}
          >
            <X size={size === 'sm' ? 14 : 16} aria-hidden />
          </button>
        )}
        {trailing}
      </div>
      {status ? (
        <span className={styles.status} aria-live="polite">
          {status}
        </span>
      ) : null}
    </div>
  )
})

export default SearchInput
